import type { Customer, Order, OrderDetail } from "@wowpress/types"

export function useWowpress() {
  // baseURL을 함수 스코프 최상단으로 이동
  const baseURL = import.meta.env.VITE_API_URL

  const login = async (username: string, password: string): Promise<string> => {
    try {
      // baseURL 변수 사용 (기존과 동일하나, 명시적으로 확인)
      const res = await fetch(`${baseURL}/auth/wowpress/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      })

      const data = await res.json()

      if (data["token"]) {
        return data["token"]
      } else {
        throw new Error("Failed to get token")
      }
    } catch (e) {
      console.error("[useWowpress] Failed to get token:", e)
      throw e
    }
  }

  // 내 정보 조회 함수
  const me = async (token: string): Promise<Customer | null> => {
    try {
      // 백엔드 프록시 /customer/me 엔드포인트 사용
      const res = await fetch(`${baseURL}/customer/me`, {
        method: "GET", // GET 메서드 명시
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      // HTTP 상태 코드로 성공 여부 판단
      if (res.status !== 200) {
        // 백엔드에서 이미 에러 메시지를 포함하여 응답할 수 있으므로, 응답 본문을 로깅해볼 수 있습니다.
        const errorData = await res.json().catch(() => ({})) // JSON 파싱 실패 대비
        console.error("[useWowpress] Failed to fetch customer info:", res.status, errorData)
        return null
      }

      // 백엔드에서 직접 Customer 객체를 반환하므로 바로 사용
      const customerData: Customer = await res.json()

      // 백엔드에서 null 이나 빈 객체를 반환하는 경우도 고려 (API 명세에 따라)
      if (!customerData || !customerData.id) {
        console.error("[useWowpress] Invalid customer data received:", customerData)
        return null
      }

      return customerData // 백엔드에서 받은 Customer 객체 반환
    } catch (e) {
      console.error("[useWowpress] Failed to fetch my info:", e)
      return null // 에러 발생 시 null 반환
    }
  }

  // 주문 정보 조회 함수
  const getOrderById = async (token: string, orderId: string): Promise<OrderDetail | null> => {
    try {
      // 백엔드 프록시 /order/:orderId 엔드포인트 사용
      const res = await fetch(`${baseURL}/order/${orderId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await res.json()
      console.log("[useWowpress] Wowpress order response:", data)

      return data as OrderDetail
    } catch (e) {
      console.error("[useWowpress] Failed to fetch order info:", e)
      return null // 에러 발생 시 null 반환
    }
  }

  // listOrders 함수 시그니처 변경: token 인자 추가
  const listOrders = async (token: string): Promise<Order[]> => {
    try {
      // token을 직접 사용하므로 useAuth 및 getIdToken 호출 제거
      // const {user} = useAuth();
      // const token = await user.value?.getIdToken();
      if (!token) {
        throw new Error("No token provided to listOrders")
      }

      const res = await fetch(`${baseURL}/order`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await res.json()
      console.log("[useWowpress] Wowpress order response:", data)

      // API에서 받은 데이터 반환 (빈 배열 대신)
      return data || []
    } catch (e) {
      console.error("[useWowpress] Failed to fetch order info:", e)
      throw e
    }
  }

  return { login, me, getOrderById, listOrders }
}
